import { useMemo } from 'react';
import {
  DepartmentAccessLevelDTO,
  useGetCurrentDepartmentTree,
  type DepartmentTreeNodeDTO,
} from '@/api/trafikportalen';
import { isStudentCollectionType } from '@/helpers/collectionTypes';
import { useAuth } from '@/stores/auth';

export const useShowOrgLevelNav = () => {
  const permissions = useAuth((s) => s.permissions) ?? [];
  const isTenantViewer = permissions.includes('tenant:viewer');

  const { data: departmentTree } = useGetCurrentDepartmentTree({
    query: { enabled: !isTenantViewer },
  });
  const departmentAccess = useAuth((s) => s.departmentAccess);

  const isOrgViewer = permissions.includes('organization:viewer');
  const isDeptUser =
    permissions.length === 0 &&
    (departmentAccess?.accessible_departments ?? 0) > 0;

  const accessibleDeptIds = useMemo(() => {
    if (!departmentAccess?.departments) return [];
    return Object.keys(departmentAccess.departments).filter(
      (id) =>
        departmentAccess.departments[id]?.access_level !==
        DepartmentAccessLevelDTO.none,
    );
  }, [departmentAccess]);

  return useMemo(() => {
    if (isTenantViewer) return false;
    if (isOrgViewer) return true;
    if (!isDeptUser || !departmentTree) return false;

    const studentDeptIds = new Set<string>();
    const collectStudentIds = (node: DepartmentTreeNodeDTO) => {
      if (isStudentCollectionType(node.type)) {
        studentDeptIds.add(node.id);
      }
      node.children.forEach(collectStudentIds);
    };
    collectStudentIds(departmentTree);

    return accessibleDeptIds.some((id) => !studentDeptIds.has(id));
  }, [
    departmentTree,
    isTenantViewer,
    isOrgViewer,
    isDeptUser,
    accessibleDeptIds,
  ]);
};
